import {
	caretDownIcon,
	caretRightIcon,
} from "../../../core/elements/icons/index.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import ReportVisitor from "../../../models/reports/reports/ReportVisitor.js";

/**
 * Visit a report to render the titles of its rows
 *
 * @param {Report} report - The report to which the rows belong
 * @param {Renderer} [itemTitleRenderer] - Takes an {@link AbstractRow}
 *   as argument and should return a title for this item
 * @param {Renderer} [dimensionsRenderer] - Takes an {@link AbstractItem}
 *  as argument and should return a renderable for its dimensions
 * @param {bool} isFlat - Should be false if the receiver should display
 *   an expand/collapse arrow next to rows with child rows. If this is `true`,
 *   `onGroupRowCollapseExpandRequest` will never be called
 * @param {function} onGroupRowCollapseExpandRequest - This function is called with a
 *   group row as argument when the user clicks on the expand/collapse icon
 */
export default class RowTitleRenderer extends ReportVisitor {
	constructor({
		report,
		itemTitleRenderer = (item) => item.getTitle(),
		dimensionsRenderer,
		isFlat,
		onGroupRowCollapseExpandRequest,
	}) {
		super(...arguments);

		this._report = report;
		this._itemTitleRenderer = itemTitleRenderer;
		this._dimensionsRenderer = dimensionsRenderer;
		this._isFlat = isFlat;
		this._onGroupRowCollapseExpandRequest = onGroupRowCollapseExpandRequest;
	}

	visitAbstractRow(row) {
		return (html) =>
			html.th(
				{ class: this._getDepthCssClass(row) },
				html.div(
					{ class: "row-title-container" },
					this._itemTitleRenderer(row),
					this._dimensionsRenderer(row),
				),
			);
	}

	visitAccountRow(accountRow) {
		return (html) =>
			html.th(
				{ class: this._getDepthCssClass(accountRow) },
				html.div(
					{ class: "row-title-container" },
					this._accountNumberRenderer({ accountRow }),
					this._itemTitleRenderer(accountRow),
					this._dimensionsRenderer(accountRow),
				),
			);
	}

	visitGroupRow(groupRow) {
		return (html) =>
			html.th(
				{ class: this._getDepthCssClass(groupRow) },
				html.div(
					{ class: "row-title-container" },
					this._collapseExpandIconRenderer({ groupRow }),
					this._itemTitleRenderer(groupRow),
					this._dimensionsRenderer(groupRow),
				),
			);
	}

	visitStaticReportRow(row) {
		return (html) =>
			html.td(
				{ class: "kpi-name" },
				html.div(
					{ class: "row-title-container" },
					this._itemTitleRenderer(row),
					this._dimensionsRenderer(row),
				),
			);
	}

	visitSpacerRow(row) {
		return (html) =>
			html.th({ class: this._getDepthCssClass(row) }, (html) =>
				html.raw("&nbsp;"),
			);
	}

	visitTextRow(row) {
		return (html) =>
			html.th(
				{ class: this._getDepthCssClass(row) },
				html.div(
					{ class: "row-title-container" },
					html.span(this._itemTitleRenderer(row)),
				),
			);
	}

	_accountNumberRenderer({ accountRow }) {
		return (html) =>
			html.span(
				{ klass: "account-number" },
				accountRow.getAccountNumber(),
			);
	}

	_getDepthCssClass(row) {
		return `data-depth-${row.getVisibilityDepth()}`;
	}

	_collapseExpandIconRenderer({ groupRow }) {
		if (this._isFlat || groupRow.getVisibleRows().length === 0) {
			return (_html) => {};
		}

		const collapsed = groupRow.isCollapsed();

		return button(
			{
				as: "div",
				onClick: () => this._onGroupRowCollapseExpandRequest(groupRow),
			},
			collapsed
				? caretRightIcon({ class: "toggle", dataTest: "expand" })
				: caretDownIcon({ class: "toggle", dataTest: "collapse" }),
		);
	}
}
